import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { streamUrl } from "@/lib/api";
import { catLabel } from "@/lib/constants";
import { useAuth } from "@/lib/auth";
import { useLoginPrompt } from "@/lib/loginPrompt";
import { useAudio } from "@/lib/audioContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Heart, Share2, ShieldCheck, Volume2, VolumeX, Upload as UploadIcon, LogIn, MessageSquare, ArrowRight } from "lucide-react";
import CastingInquiryModal from "@/components/CastingInquiryModal";
import BackButton from "@/components/BackButton";
import ReferralChip from "@/components/ReferralChip";

export default function PublicFeed() {
  const { user } = useAuth();
  const { requireAuth, openModal: openLoginModal } = useLoginPrompt();
  const nav = useNavigate();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inquiryFor, setInquiryFor] = useState(null); // {username, video_id?}

  const load = async () => {
    try {
      const { data } = await api.get("/videos/feed?limit=30");
      setVideos(data.videos || []);
    } catch {
      setVideos([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openUpload = () => {
    if (user) nav("/app/upload");
    else openLoginModal();
  };
  const guardedCast = (payload) => requireAuth(() => setInquiryFor(payload));

  return (
    <div data-testid="public-feed-page" className="min-h-screen bg-black text-white grain">
      {/* Sticky top bar (glass) */}
      <div className="sticky top-0 z-40 backdrop-blur bg-black/60 border-b border-zinc-900">
        <div className="max-w-2xl mx-auto px-3 h-14 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <BackButton fallback="/" label="Back" compact/>
            <Link to="/" data-testid="public-feed-home" className="inline-flex items-center gap-2 shrink-0">
              <Logo size="sm"/>
            </Link>
          </div>
          {!user && (
            <div className="flex items-center gap-2">
              <button type="button" onClick={openLoginModal} data-testid="public-feed-login" className="text-sm text-zinc-300 hover:text-white">Log in</button>
              <button type="button" onClick={openLoginModal} data-testid="public-feed-signup" className="text-sm rounded-full bg-[#C79A2B] hover:bg-[#D3A63A] text-black px-3 py-1.5 font-semibold">Sign up</button>
            </div>
          )}
        </div>
      </div>

      {/* Floating "Invite & earn coins" chip (only if not authenticated) */}
      {!user && (
        <div className="fixed top-16 right-3 z-30">
          <ReferralChip isAuthed={false}/>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-[60vh] text-zinc-400 text-sm">Loading feed…</div>
      ) : videos.length === 0 ? (
        <EmptyPublic openUpload={openUpload}/>
      ) : (
        <div className="feed-container pb-32">
          {videos.map((v) => (
            <PublicFeedItem
              key={v.id}
              video={v}
              isAuthed={!!user}
              requireAuth={requireAuth}
              onCast={() => guardedCast({ username: v.username, video_id: v.id })}
            />
          ))}
        </div>
      )}

      {/* Sticky bottom Upload CTA */}
      <button
        data-testid="public-feed-upload-cta"
        onClick={openUpload}
        className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 h-14 px-7 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold shadow-2xl shadow-[#DC2626]/40 border-2 border-white/10 flex items-center gap-2 transition-all active:scale-95"
      >
        {user ? <UploadIcon size={18}/> : <LogIn size={18}/>}
        {user ? "Upload your performance" : "Upload your performance"}
      </button>

      <CastingInquiryModal
        open={!!inquiryFor}
        target={inquiryFor}
        onClose={() => setInquiryFor(null)}
      />
    </div>
  );
}

function EmptyPublic({ openUpload }) {
  return (
    <div data-testid="public-feed-empty" className="max-w-2xl mx-auto px-6 pt-8 pb-32">
      <div className="rounded-3xl border border-[#DC2626]/30 bg-gradient-to-br from-[#DC2626]/12 via-[#180D0D] to-[#0A0605] p-8 relative overflow-hidden">
        <div className="pointer-events-none absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#DC2626]/25 blur-3xl"/>
        <p className="text-xs uppercase tracking-[0.2em] text-[#DC2626]">Curtain up</p>
        <h2 className="mt-2 font-display text-3xl font-bold leading-tight">Bharat ka stage <span className="gold-text">taiyaar hai.</span></h2>
        <p className="mt-3 text-sm text-zinc-300">Be the first to upload a performance. Casting directors are watching.</p>
        <button onClick={openUpload} className="mt-5 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold px-6 h-11">Upload your performance</button>
      </div>
    </div>
  );
}

function PublicFeedItem({ video, isAuthed, requireAuth, onCast }) {
  const { audioEnabled } = useAudio();
  const [likes, setLikes] = useState(video.likes || 0);
  const [liked, setLiked] = useState(false);
  // Iter 44/45: per-video mute; default = "unmuted only after first user
  // interaction anywhere on the site" (AudioContext).
  const [muted, setMuted] = useState(!audioEnabled);
  const [userTouched, setUserTouched] = useState(false);
  useEffect(() => {
    if (audioEnabled && !userTouched) setMuted(false);
  }, [audioEnabled, userTouched]);
  const ref = useRef(null);
  const toggleMute = (e) => {
    e?.stopPropagation?.();
    setUserTouched(true);
    setMuted((m) => {
      requestAnimationFrame(() => { ref.current?.play?.().catch(() => {}); });
      return !m;
    });
  };

  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) el.play().catch(() => {});
      else el.pause();
    }, { threshold: 0.6 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const doLike = async () => {
    try {
      const { data } = await api.post(`/videos/${video.id}/like`);
      setLiked(data.liked);
      setLikes(l => l + (data.liked ? 1 : -1));
    } catch {}
  };
  const like = () => requireAuth(doLike);
  const share = async () => {
    const url = `${window.location.origin}/v/${video.id}`;
    if (navigator.share) { try { await navigator.share({ title: video.title || "NextScene", url }); return; } catch {} }
    await navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };

  return (
    <div data-testid={`public-feed-item-${video.id}`} className="feed-item relative bg-black">
      <video
        ref={ref}
        src={streamUrl(video.storage_path)}
        muted={muted}
        loop
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent pointer-events-none"/>

      {/* Right actions — mute moved into the column so it never overlaps like */}
      <div className="absolute right-3 top-4 bottom-40 z-10 flex flex-col gap-3 items-center">
        <button data-testid="public-mute-btn" onClick={toggleMute} className="w-11 h-11 rounded-full glass border border-zinc-700 text-white flex items-center justify-center">
          {muted ? <VolumeX size={18}/> : <Volume2 size={18}/>}
        </button>
        <div className="flex-1"/>
        <button data-testid="public-like-btn" onClick={like} className={`w-11 h-11 rounded-full glass border ${liked ? "border-[#DC2626]" : "border-zinc-700"} flex items-center justify-center text-white`}>
          <Heart size={22} fill={liked ? "#EF4444" : "none"}/>
        </button>
        <span className="text-xs text-white -mt-2">{likes}</span>
        <button data-testid="public-share-btn" onClick={share} className="w-11 h-11 rounded-full glass border border-zinc-700 text-white flex items-center justify-center">
          <Share2 size={20}/>
        </button>
        <button
          data-testid="public-cast-btn"
          onClick={onCast}
          title="Cast this talent"
          className="w-11 h-11 rounded-full bg-[#DC2626] hover:bg-[#EF4444] border-2 border-white/20 text-white flex items-center justify-center shadow-lg"
        >
          <MessageSquare size={18}/>
        </button>
      </div>

      <div className="absolute left-0 right-14 bottom-0 z-10 p-4 pb-24 text-white">
        <Link to={`/${video.username}`} className="inline-flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-[#DC2626]/20 border border-[#DC2626]/50 flex items-center justify-center text-[#DC2626] font-semibold">
            {(video.name || "?").slice(0,1).toUpperCase()}
          </div>
          <div className="leading-tight">
            <div className="flex items-center gap-1">
              <p className="text-sm font-semibold">{video.name}</p>
              {video.is_verified && <ShieldCheck size={12} className="text-[#F5C462]"/>}
            </div>
            <p className="text-xs text-zinc-400">@{video.username} · {catLabel(video.category)}</p>
          </div>
        </Link>
        {video.title && <h3 className="mt-3 font-display text-lg font-semibold">{video.title}</h3>}
        {video.description && <p className="mt-1 text-sm text-zinc-300 line-clamp-2">{video.description}</p>}
        <button
          onClick={onCast}
          data-testid="cast-this-talent-btn"
          className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#DC2626] text-white font-semibold px-4 h-9 hover:bg-[#EF4444] transition-colors"
        >
          <MessageSquare size={14}/> Cast this talent
        </button>
      </div>
    </div>
  );
}
